// Turn a month report (buildMonth's `days`) into a WorkCalendar overlay.
//
// Shared by the employee's own calendar and the cockpit's per-person one, so
// the two cannot label the same day differently. The server already decided
// every status; this only chooses the words and the tone.

const PORTION_LABEL = {
  full: 'full day',
  first_half: 'first half',
  second_half: 'second half',
};

const hhmm = (m) => `${Math.floor((m || 0) / 60)}h ${String(Math.round((m || 0) % 60)).padStart(2, '0')}m`;

const TONE = {
  present: 'present',
  half_day: 'half',
  absent: 'absent',
  leave: 'leave',
  holiday: 'holiday',
  week_off: 'off',
  upcoming: 'upcoming',
};

/**
 * @param {Array}  days      from GET /api/attendance/me | /user/:id
 * @param {Array} [pending]  the employee's own PENDING requests, so a future
 *                           application shows on their calendar before anyone
 *                           has decided it. Omitted for the manager's view,
 *                           which reads the register rather than intentions.
 * @returns {Object} { dateKey → { tone, label, detail, pending, dot } }
 */
export function buildMonthOverlay(days = [], pending = []) {
  const overlay = {};

  for (const d of days) {
    // A leave day says WHICH leave, not just "leave" — that is the whole point
    // of hovering it.
    if (d.leave) {
      const portion = PORTION_LABEL[d.leave.portion] || 'full day';
      overlay[d.dateKey] = {
        tone: 'leave',
        label: d.leave.typeName,
        detail: d.workedMinutes
          // They took leave and came in anyway. Saying only "leave" would
          // contradict the hours shown beside it.
          ? `${portion} · worked ${hhmm(d.workedMinutes)} anyway`
          : `${portion} · approved`,
        dot: true,
      };
      continue;
    }

    if (d.status === 'holiday' || d.status === 'week_off') continue; // the rules already draw these

    if (d.status === 'present' || d.status === 'half_day') {
      overlay[d.dateKey] = {
        tone: TONE[d.status],
        label: d.status === 'half_day' ? 'Half day' : 'Present',
        detail: `worked ${hhmm(d.workedMinutes)}`
          + (d.overtimeMinutes ? ` · ${hhmm(d.overtimeMinutes)} overtime` : ''),
      };
    } else if (d.status === 'absent') {
      overlay[d.dateKey] = { tone: 'absent', label: 'Absent', detail: 'no record for this day' };
    } else if (d.status === 'upcoming') {
      overlay[d.dateKey] = { tone: 'upcoming', label: 'Upcoming', detail: '' };
    }
  }

  // Pending applications last, so an undecided request is visible over whatever
  // the day would otherwise have been. It must NOT overwrite an approved leave
  // — the same date can carry both when someone splits a day.
  for (const r of pending) {
    for (const day of r.days || []) {
      const existing = overlay[day.dateKey];
      if (existing?.tone === 'leave' && !existing.pending) continue;
      overlay[day.dateKey] = {
        tone: 'leave',
        pending: true,
        dot: true,
        label: `${r.leaveTypeName} — awaiting approval`,
        detail: `${PORTION_LABEL[day.portion] || 'full day'} · applied ${
          new Date(r.createdAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
        }`,
      };
    }
  }

  return overlay;
}

export const LEAVE_STATUS = {
  pending: { label: 'Pending', cls: 'att-lv-s-pending' },
  approved: { label: 'Approved', cls: 'att-lv-s-approved' },
  rejected: { label: 'Rejected', cls: 'att-lv-s-rejected' },
  cancelled: { label: 'Cancelled', cls: 'att-lv-s-cancelled' },
};

export const fmtDay = (k) => (k
  ? new Date(`${k}T00:00:00Z`).toLocaleDateString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC',
  })
  : '—');

// "3 Aug" or "3–7 Aug 2026" — a range reads as one thing, not two dates.
export function fmtRange(from, to) {
  if (!from) return '—';
  if (from === to) return fmtDay(from);
  const a = new Date(`${from}T00:00:00Z`);
  const b = new Date(`${to}T00:00:00Z`);
  const sameMonth = a.getUTCMonth() === b.getUTCMonth() && a.getUTCFullYear() === b.getUTCFullYear();
  if (sameMonth) {
    return `${a.getUTCDate()}–${b.getUTCDate()} ${b.toLocaleDateString(undefined, { month: 'short', year: 'numeric', timeZone: 'UTC' })}`;
  }
  return `${fmtDay(from)} – ${fmtDay(to)}`;
}

export { PORTION_LABEL, hhmm };
